import { CheckSquare, Square, UserPlus, Shield, Trash2, ShieldOff } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Extracted from /pages/SiteAdmin.jsx in iter84.40 (tech debt sweep #4
// batch 3). Pure UI; the parent owns the selection state, eligibility
// computation, and the loaders. The list-row delete button calls back
// into the parent's `onAdminDeleted` so it can refresh `siteAdmins` —
// avoids leaking `setSiteAdmins` directly into the child.
export default function AdminsTab({
    admin,
    siteAdmins,
    eligibleAdminIds,
    selectedAdminIds,
    adminSelectable,
    onToggleAll,
    onToggleAdmin,
    onOpenInvite,
    onAdminDeleted,
}) {
    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <p className="text-sm text-zinc-400">{siteAdmins.length} site admin(s)</p>
                <div className="flex gap-2 flex-wrap">
                    {admin.is_super && eligibleAdminIds.length > 0 && (
                        <button
                            data-testid="select-all-admins-btn"
                            onClick={onToggleAll}
                            className="brutal-btn-ghost text-xs py-2"
                        >
                            {selectedAdminIds.size === eligibleAdminIds.length
                                ? <><CheckSquare className="w-3.5 h-3.5 text-cyber" /> Deselect all</>
                                : <><Square className="w-3.5 h-3.5" /> Select all</>}
                        </button>
                    )}
                    {admin.is_super && (
                        <button data-testid="invite-admin-btn" onClick={onOpenInvite} className="brutal-btn-secondary text-xs py-2">
                            <UserPlus className="w-3.5 h-3.5" /> Invite admin
                        </button>
                    )}
                </div>
            </div>
            <div className="space-y-2" data-testid="site-admins-list">
                {adminSelectable.map(sa => {
                    const isSelf = sa.id === admin.id;
                    const canDelete = admin.is_super && !sa._disabledReason;
                    const isSelected = selectedAdminIds.has(sa.id);
                    return (
                        <div key={sa.id} data-testid={`site-admin-row-${sa.id}`} className={`brutal-card-light p-3 flex items-center justify-between gap-3 ${isSelected ? "border-cyber" : ""}`}>
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                                {admin.is_super && (
                                    <button
                                        type="button"
                                        data-testid={`select-admin-${sa.id}-checkbox`}
                                        onClick={() => !sa._disabledReason && onToggleAdmin(sa.id)}
                                        disabled={!!sa._disabledReason}
                                        className="mt-0.5 shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                                        aria-label={isSelected ? "Deselect" : "Select"}
                                        aria-pressed={isSelected}
                                        title={sa._disabledReason === "self" ? "Cannot delete yourself" : sa._disabledReason === "last_super" ? "Cannot delete the last super-admin" : ""}
                                    >
                                        {isSelected
                                            ? <CheckSquare className="w-5 h-5 text-cyber" strokeWidth={2.5} />
                                            : <Square className="w-5 h-5 text-zinc-500 hover:text-zinc-300" strokeWidth={2} />}
                                    </button>
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="font-bold truncate">
                                        {sa.email} {isSelf && <span className="text-[10px] text-cyber ml-1">(you)</span>}
                                    </p>
                                    <p className="text-[10px] text-zinc-500">
                                        {sa.name} · {(() => {
                                            const r = sa.role || (sa.is_super ? "platform_admin" : "tenant_admin");
                                            if (r === "platform_admin") return <span className="text-cyber font-bold">PLATFORM ADMIN</span>;
                                            if (r === "platform_developer") return <span className="text-volt font-bold">PLATFORM DEVELOPER</span>;
                                            return "TENANT ADMIN";
                                        })()} · since {new Date(sa.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {sa.is_super && <Shield className="w-4 h-4 text-cyber" />}
                                {admin.is_super && !isSelf && (
                                    <button
                                        data-testid={`reset-totp-${sa.id}-btn`}
                                        onClick={async () => {
                                            if (!window.confirm(`Clear ${sa.email}'s two-factor setup? They'll be able to sign in with password alone and re-enroll. An audit-log row will be written.`)) return;
                                            try {
                                                await api.authSuperAdminResetTotp(sa.id);
                                                toast.success(`Cleared 2FA on ${sa.email} — they can now re-enroll`);
                                            } catch (err) {
                                                const msg = err.response?.data?.detail;
                                                toast.error(typeof msg === "string" ? msg : "Reset didn't go through");
                                            }
                                        }}
                                        className="brutal-btn-ghost p-2"
                                        title="Reset 2FA (super admin only) — clears their authenticator so they can re-enroll"
                                    >
                                        <ShieldOff className="w-4 h-4 text-volt" />
                                    </button>
                                )}
                                {canDelete && (
                                    <button
                                        data-testid={`delete-site-admin-${sa.id}-btn`}
                                        onClick={async () => {
                                            if (!window.confirm(`Permanently remove ${sa.email}? They will lose all access immediately.`)) return;
                                            try {
                                                await api.authDeleteSiteAdmin(sa.id);
                                                toast.success(`Removed ${sa.email}`);
                                                const list = await api.authListSiteAdmins();
                                                onAdminDeleted(list);
                                            } catch (err) {
                                                const msg = err.response?.data?.detail;
                                                toast.error(typeof msg === "string" ? msg : "Failed");
                                            }
                                        }}
                                        className="brutal-btn-ghost p-2"
                                        title="Delete site admin"
                                    >
                                        <Trash2 className="w-4 h-4 text-heat" />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
